import { memo, useState, useEffect, useRef, useMemo } from "react";
import { motion, useMotionValue, AnimatePresence } from "framer-motion";
import { RefreshCw, RotateCcw, Zap, GripVertical, Info, Crosshair } from "lucide-react";
import { POSITIONS, BASE_COORDS, SHOW_COORDINATES } from "./constants";
import { getBatterIndex, getLineup, formatEra} from "./utils";
import { BASE_ORDER, basesEqual as _basesEqual, computeBaseSteps } from "@/utils/baseRunnerUtils";
export { computeBaseSteps } from "@/utils/baseRunnerUtils";

const LS_HOME_FIELDERS = 'junkball_homeFielderPositions';
const LS_AWAY_FIELDERS = 'junkball_awayFielderPositions';
const LS_RUNNER_POSITIONS = 'junkball_runnerPositions';
const LS_BATTER_POSITIONS = 'junkball_batterPositions';

function loadJSON(key, fallback = {}) {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : fallback; } catch { return fallback; }
}
function saveJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function getLastName(name) {
  if (!name) return '—';
  const parts = name.split(' ');
  if (parts.length <= 1) return name;
  // Keep "Jr." / "Sr." / "III" etc. attached to the surname
  const suffix = parts[parts.length - 1];
  if (['Jr.', 'Jr', 'Sr.', 'Sr', 'II', 'III', 'IV'].includes(suffix) && parts.length > 2) {
    return parts.slice(-2).join(' ');
  }
  return suffix;
}

// ── Fielder Token ──────────────────────────────────────────────────
function FielderToken({ player, pos, type, isActive, onAction, onCloseOthers, menuOpen, isSelectable, customPosition, onDragEnd, containerRef, canEdit = true, hotdog = false, elevated = false, toughHopHighlight = false, toughHopSelected = false, toughHopColorHex = null, toughHopOnClick = null, directionalHittingHighlight = false, directionalHittingSelected = false, directionalHittingColorHex = null, directionalHittingOnClick = null }) {
  const defaultPosition = POSITIONS[pos] || POSITIONS.BAT_R;
  const position = customPosition || defaultPosition;
  const lastName = getLastName(player?.name);
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const isDraggable = type === "pitcher" || type === "batter" || type === "fielder";

  const getBg = () => {
    if (toughHopSelected || toughHopHighlight) return ""; // team-color inline style applied below
    if (directionalHittingSelected) return ""; // team-color inline style applied below
    if (directionalHittingHighlight) return ""; // team-color inline style applied below
    if (type === "batter") return "bg-gradient-to-br from-amber-400 to-orange-500 border-amber-300 text-black shadow-lg shadow-amber-500/40";
    if (type === "pitcher") return "bg-gradient-to-br from-red-500 to-rose-600 border-red-400 text-white shadow-lg shadow-red-500/40";
    if (isSelectable) return "bg-gradient-to-br from-emerald-500 to-emerald-600 border-emerald-400 text-white shadow-lg shadow-emerald-500/40";
    return "bg-gradient-to-br from-slate-600 to-slate-700 border-slate-400 text-white shadow-md";
  };

  // Team-color styles for fielder target highlight (inline to support dynamic hex).
  const thInlineStyle = (toughHopHighlight || toughHopSelected) && toughHopColorHex
    ? {
        background: toughHopSelected
          ? toughHopColorHex
          : `${toughHopColorHex}55`,
        borderColor: toughHopColorHex,
        color: "white",
        boxShadow: toughHopSelected
          ? `0 0 14px 3px ${toughHopColorHex}99`
          : `0 0 8px 1px ${toughHopColorHex}55`,
      }
    : {};
  const dhInlineStyle = (directionalHittingHighlight || directionalHittingSelected) && directionalHittingColorHex
    ? {
        background: directionalHittingSelected
          ? directionalHittingColorHex
          : `${directionalHittingColorHex}55`,
        borderColor: directionalHittingColorHex,
        color: "white",
        boxShadow: directionalHittingSelected
          ? `0 0 14px 3px ${directionalHittingColorHex}99`
          : `0 0 8px 1px ${directionalHittingColorHex}55`,
      }
    : {};

  const handleClick = (e) => {
    if (isDragging) return;
    e.stopPropagation();
    if (toughHopOnClick) { toughHopOnClick(player); return; }
    if (directionalHittingOnClick) { directionalHittingOnClick(player); return; }
    if (isSelectable) return;
    onCloseOthers?.(pos);
  };
  const opacity = isSelectable === false ? 'opacity-40' : '';

  const handleDragEnd = (event, info) => {
    const didDrag = Math.abs(info.offset.x) > 3 || Math.abs(info.offset.y) > 3;
    setTimeout(() => setIsDragging(false), 50);
    if (didDrag && onDragEnd && isDraggable && containerRef?.current) {
      const r = containerRef.current.getBoundingClientRect();
      const newX = ((position.x / 100) * r.width + info.offset.x) / r.width * 100;
      const newY = ((position.y / 100) * r.height + info.offset.y) / r.height * 100;
      x.set(0); y.set(0);
      onDragEnd(pos, { x: Math.max(2, Math.min(98, newX)), y: Math.max(2, Math.min(98, newY)) });
    } else { x.set(0); y.set(0); }
  };

  useEffect(() => { x.set(0); y.set(0); }, [position.x, position.y, x, y]);

  return (
    <motion.div
      className="absolute"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        zIndex: isDragging ? 150 : (menuOpen ? 200 : (elevated ? 70 : ((toughHopHighlight || toughHopSelected || directionalHittingHighlight || directionalHittingSelected) ? 80 : 10))),
        x, y
      }}
      drag={isDraggable && !isSelectable}
      dragMomentum={false}
      dragElastic={0}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={handleDragEnd}
    >
      {/* Inner wrapper: translateX(-50%) centers horizontally, translateY(-22px) = half of w-11 circle centers the circle on the point */}
      <div className={`flex flex-col items-center ${opacity}`} style={{ transform: 'translate(-50%, -22px)' }}>
        <div
          className={`relative ${(toughHopOnClick || directionalHittingOnClick) ? 'cursor-crosshair' : isSelectable !== false ? 'cursor-pointer' : 'cursor-not-allowed'} ${isDraggable && !isSelectable && !toughHopOnClick && !directionalHittingOnClick ? 'cursor-grab active:cursor-grabbing' : ''} group`}
          onClick={handleClick}
        >
          <div data-streaker-obstacle="true" className={`w-11 h-11 rounded-full flex items-center justify-center text-[11px] font-black border-2 transition-all duration-200 group-hover:scale-105 ${getBg()}`} style={{ ...thInlineStyle, ...dhInlineStyle }}>
            {pos.replace('BAT_L', 'BAT').replace('BAT_R', 'BAT')}
          </div>
          {hotdog && (
            <div className="absolute -bottom-0.5 -right-0.5 text-sm leading-none pointer-events-none select-none">🌭</div>
          )}
          {(isActive || isSelectable) && (
            <motion.div
              animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className={`absolute inset-0 rounded-full ${isSelectable ? 'bg-emerald-400/50' : 'bg-white/40'}`}
            />
          )}
          {toughHopSelected && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center text-[9px] font-black text-black shadow z-10">✓</div>
          )}
          {(toughHopHighlight && !toughHopSelected) && (
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ repeat: Infinity, duration: 1.2 }}
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: toughHopColorHex ? `${toughHopColorHex}55` : "rgba(99,102,241,0.4)" }}
            />
          )}
          {directionalHittingSelected && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center text-[9px] font-black text-black shadow z-10">✓</div>
          )}
          {(directionalHittingHighlight && !directionalHittingSelected) && (
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ repeat: Infinity, duration: 1.2 }}
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: directionalHittingColorHex ? `${directionalHittingColorHex}55` : "rgba(167,139,250,0.4)" }}
            />
          )}
          {isDraggable && !isSelectable && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <GripVertical size={10} className="text-white" />
            </div>
          )}
        </div>
        <div className="mt-0.5 text-center bg-black/80 px-1.5 py-0.5 rounded-full backdrop-blur-sm pointer-events-none">
          <span className="text-[9px] text-white font-semibold">{lastName}</span>
          {SHOW_COORDINATES && <div className="text-[7px] text-cyan-400">({Math.round(position.x)},{Math.round(position.y)})</div>}
        </div>

        {menuOpen && !isSelectable && !isDragging && (
          <div onClick={(e) => e.stopPropagation()}
            className="absolute left-full top-0 ml-2 z-[999] bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-1 min-w-[140px]">
            <button
              onClick={() => { onCloseOthers?.(null); if (canEdit) onAction?.('substitute', player); }}
              disabled={!canEdit}
              title={canEdit ? "Substitute" : (type === "batter" ? "Only the batting team can sub batters" : "Only the fielding team can sub fielders")}
              className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 ${canEdit ? "hover:bg-slate-800 text-slate-200" : "text-slate-600 cursor-not-allowed"}`}>
              <RefreshCw size={12} /> Substitute
            </button>
            <button onClick={() => { onCloseOthers?.(null); onAction?.('details', player); }}
              className="w-full px-3 py-2 text-left text-xs hover:bg-slate-800 flex items-center gap-2 text-slate-200">
              <Zap size={12} /> Player Card
            </button>
            {isDraggable && (
              <button onClick={() => { onCloseOthers?.(null); onDragEnd?.(pos, null); }}
                className="w-full px-3 py-2 text-left text-xs hover:bg-slate-800 flex items-center gap-2 text-slate-200">
                <RotateCcw size={12} /> Reset Position
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Runner Token ──────────────────────────────────────────────────
function Runner({
  base, runner,
  canSteal = false, onDeclareSteal, onShowDetails, menuOpen, onMenuToggle,
  pickoffSelectMode = false, isPickoffTarget = false, onPickoffSelect = null,
}) {
  const position = BASE_COORDS[base];
  const lastName = getLastName(runner?.name) || '?';
  if (!runner || !position) return null;

  const isHome = base === 'home';
  const baseLabel = base === 'first' ? '1B' : base === 'second' ? '2B' : base === 'third' ? '3B' : 'H';
  const isPickoffClickable = pickoffSelectMode && !isHome && !!onPickoffSelect;

  const handleClick = (e) => {
    if (isHome) return;
    e.stopPropagation();
    if (isPickoffClickable) {
      onPickoffSelect(base);
      return;
    }
    onMenuToggle?.(menuOpen ? null : base);
  };

  // Visual state for pickoff mode. The targeted runner keeps the normal amber tile —
  // a crosshair icon inside the tile signals targeting instead of a color change.
  let tileClass = "w-9 h-9 rounded-full border-2 shadow-lg flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-400/60 border-white";
  if (pickoffSelectMode && !isHome && !isPickoffTarget) {
    // All un-targeted runners pulse during selection so the fielder sees the choices.
    tileClass = "w-9 h-9 rounded-full border-2 shadow-lg flex items-center justify-center bg-gradient-to-br from-amber-300 to-yellow-400 shadow-yellow-300/80 border-yellow-200 animate-pulse";
  }

  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 20 }}
      className="absolute"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        zIndex: isHome ? 90 : (menuOpen || isPickoffTarget ? 200 : 100),
      }}
      onClick={handleClick}
    >
      {/* translate(-50%, -18px): centers the w-9 circle exactly on the base coordinate */}
      <div
        className={`flex flex-col items-center ${isPickoffClickable ? "cursor-crosshair" : "cursor-pointer"}`}
        style={{ transform: 'translate(-50%, -18px)' }}
      >
        <div className={`flex flex-col items-center transition-transform ${isPickoffClickable ? "hover:scale-125" : "hover:scale-110"}`}>
          <div data-streaker-obstacle="true" className={tileClass}>
            {isPickoffTarget ? (
              <Crosshair className="w-5 h-5 text-black" strokeWidth={2.5} />
            ) : (
              <span className="text-[11px] font-black text-black">{baseLabel}</span>
            )}
          </div>
          {!isHome && (
            <div className="mt-0.5 px-1.5 py-0.5 rounded text-center pointer-events-none bg-black/80">
              <div className="text-[8px] text-white font-semibold whitespace-nowrap">{lastName}</div>
            </div>
          )}
        </div>

        {menuOpen && !pickoffSelectMode && (
          <div onClick={(e) => e.stopPropagation()}
            className="absolute left-full top-0 ml-2 z-[999] bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-1 min-w-[160px]">
            <button
              onClick={() => { onMenuToggle?.(null); onShowDetails?.(runner); }}
              className="w-full px-3 py-2 text-left text-xs hover:bg-slate-800 flex items-center gap-2 text-slate-200">
              <Info size={12} /> Player Card
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// basesEqualByIds — alias to imported helper for local use
const basesEqualByIds = _basesEqual;

// ── Main DiamondField ──────────────────────────────────────────────
function DiamondField({ game, currentPhase, activePositions = [], gameStats = {}, pitchClock = null, pitchClockKind = "pitch", canSteal = false, canEditFielding = true, canEditBatter = true, holdBases = false, liveBases = null, fieldingRunsScored = null, fieldingOutcome = null, onStatsClick, onPitcherClick, onFielderAction, onDeclareSteal, onRunnerAnimationSettled, hotdogTrigger = null, pickoffSelectMode = false, pickoffSelectedBase = null, onPickoffSelectRunner = null, stepDuration = 350, toughHopSelectMode = false, toughHopEligibleIds = [], toughHopSelectedId = null, toughHopColorHex = null, onToughHopSelect = null, directionalHittingSelectMode = false, directionalHittingEligibleIds = [], directionalHittingSelectedId = null, directionalHittingColorHex = null, onDirectionalHittingSelect = null }) {
  const fieldingHotdogIds = new Set(
    game?.state?.half === "top" ? (game?.home_hotdog_players || []) : (game?.away_hotdog_players || [])
  );
  const battingHotdogIds = new Set(
    game?.state?.half === "top" ? (game?.away_hotdog_players || []) : (game?.home_hotdog_players || [])
  );
  const isFielderHotdog = (player) => !!player?.id && fieldingHotdogIds.has(player.id);
  const isBatterHotdog = (player) => !!player?.id && battingHotdogIds.has(player.id);

  const [openMenu, setOpenMenu] = useState(null);
  const containerRef = useRef(null);

  // Hotdog floater particles — spawned when the John Kruk card is played.
  // Each entry: { id, pos, delay } where pos is a POSITIONS key.
  // Fielding team: cascades across all 9 positions with a staggered delay.
  // Batting team: single floater on the current batter only.
  // Infield-to-outfield cascade order: P → C → 1B → 2B → 3B → SS → LF → CF → RF
  const HOTDOG_POSITIONS = ["P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"];
  const TILE_STAGGER = 0.25;  // seconds between each tile firing
  const RISE_DURATION = 1.2;  // float-up-and-fade duration per hotdog
  const [hotdogFloaters, setHotdogFloaters] = useState([]);
  useEffect(() => {
    if (!hotdogTrigger?.key) return;
    const fieldingRole = game.state.half === "top" ? "home" : "away";
    const isCallerFielding = hotdogTrigger.role === fieldingRole;
    const shuffled = [...HOTDOG_POSITIONS].sort(() => Math.random() - 0.5);
    const particles = isCallerFielding
      ? shuffled.map((pos, i) => ({
          id: `hd-${hotdogTrigger.key}-${pos}`,
          pos,
          delay: i * TILE_STAGGER,
        }))
      : [{ id: `hd-${hotdogTrigger.key}-bat`, pos: "__batter__", delay: 0 }];
    setHotdogFloaters(particles);
    const maxDelay = (particles.length - 1) * TILE_STAGGER;
    const clearAfter = (maxDelay + RISE_DURATION + 0.2) * 1000;
    const t = setTimeout(() => setHotdogFloaters([]), clearAfter);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotdogTrigger]);

  // Animated baserunning: when the live state.bases changes, step through intermediate bases
  // (runner appears on 1B → 2B → 3B → home) before settling at the final state.
  const [displayedBases, setDisplayedBases] = useState({ ...game.state.bases, home: null });
  const baseAnimRef = useRef([]);
  // Hash by RUNNER IDs so the effect doesn't re-fire on object-reference changes that don't
  // actually change the bases content. Without this, every game_state broadcast (which produces
  // a fresh object reference) cancels the pending animation mid-trot and the runner never
  // visibly settles on the final base.
  const basesHash = useMemo(
    () => {
      const b = liveBases ?? game.state.bases;
      return BASE_ORDER.map(k => b[k]?.id || "_").join("|");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveBases, game.state.bases]
  );
  const prevBattingScoreRef = useRef(null); // fallback for local (non-online) games
  useEffect(() => {
    // Hold runners in place while a resolution animation is playing so they don't
    // hop forward before the dice reveal the outcome.
    if (holdBases) return;
    const newBases = liveBases ?? game.state.bases;

    // Use the authoritative runs_scored from the fielding resolution when available.
    // Falls back to score-delta for local/non-online plays where the prop isn't set.
    let runsScored;
    if (fieldingRunsScored != null) {
      runsScored = fieldingRunsScored;
    } else {
      const battingTeam = game.state.half === "top" ? "away" : "home";
      const currentBattingScore = battingTeam === "home" ? game.state.home_score : game.state.away_score;
      const prevBattingScore = prevBattingScoreRef.current ?? currentBattingScore;
      runsScored = Math.max(0, currentBattingScore - prevBattingScore);
      prevBattingScoreRef.current = currentBattingScore;
    }

    // ── Batter injection ───────────────────────────────────────────────────────
    // The batter is not tracked in game.state.bases, so two outcomes require us to
    // inject them into the animation manually:
    //   HR  — batter runs all 4 bases (injectedBatterDest=4). Only when fieldingOutcome
    //         is set (online play) and runsScored > 0; local games fall back to nothing.
    //   DP  — batter may also be out at 1st (injectedBatterDest=1) when they don't
    //         appear in newBases (i.e. the DP config put them out).
    const batterInNewBases = !!displayedBatter?.id &&
      Object.values(newBases).some(r => r?.id === displayedBatter.id);
    // Force-out (fielder's choice): batter is safe at first while a runner ahead was put out.
    // Treat the same as a DP so the disappearing runner animates one base forward to where they
    // were tagged, rather than freezing at their current base and vanishing.
    const batterSafeAtFirst = !!newBases.first && newBases.first.id === displayedBatter?.id;
    const isDPPlay = fieldingOutcome === "double_play" || (fieldingOutcome === "out" && batterSafeAtFirst);
    let injectedBatter = null;
    let injectedBatterDest = null;
    if (fieldingOutcome === "hr" && runsScored > 0 && !batterInNewBases && displayedBatter) {
      injectedBatter = displayedBatter;
      injectedBatterDest = 4; // full circuit
    } else if (isDPPlay && !batterInNewBases && displayedBatter) {
      injectedBatter = displayedBatter;
      injectedBatterDest = 1; // out at first
    }

    const noBaseChange = basesEqualByIds(displayedBases, newBases);
    if (noBaseChange && !injectedBatter) {
      // Nothing to animate — settle immediately and clear any stale animation ref.
      setDisplayedBases({ ...newBases, home: null });
      onRunnerAnimationSettled?.();
      return;
    }

    const steps = computeBaseSteps(displayedBases, newBases, runsScored, {
      isDPPlay,
      injectedBatter,
      injectedBatterDest,
    });
    // Cancel any prior pending steps to avoid stacking sequences
    baseAnimRef.current.forEach(t => clearTimeout(t));
    baseAnimRef.current = [];
    if (steps.length === 0) {
      setDisplayedBases({ ...newBases, home: null });
      onRunnerAnimationSettled?.();
      return;
    }
    steps.forEach((step, i) => {
      const t = setTimeout(() => setDisplayedBases(step), (i + 1) * stepDuration);
      baseAnimRef.current.push(t);
    });
    // Final settle at the truth state (covers any drift from the algorithm). Fires
    // onRunnerAnimationSettled so GameBoard can defer the half-inning hand-refresh
    // modal until after the runner trot finishes — otherwise the modal can open
    // while runners are still hopping to their final bases.
    const finalT = setTimeout(() => {
      setDisplayedBases({ ...newBases, home: null });
      onRunnerAnimationSettled?.();
    }, (steps.length + 1) * stepDuration);
    baseAnimRef.current.push(finalT);
    return () => {
      // Cancel in-flight timers. If any were pending, the animation was interrupted
      // (next pitch arrived before this one finished). Jump to the settled state and
      // fire the callback so GameBoard flags (runnerAnimationActive etc.) don't get stuck.
      if (baseAnimRef.current.length > 0) {
        baseAnimRef.current.forEach(t => clearTimeout(t));
        baseAnimRef.current = [];
        setDisplayedBases({ ...newBases, home: null });
        onRunnerAnimationSettled?.();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basesHash, holdBases]);

  const fieldingTeam = game.state.half === "top" ? game.home_team : game.away_team;
  const battingTeam = game.state.half === "top" ? game.away_team : game.home_team;
  const batters = getLineup(battingTeam);
  const batterIndex = getBatterIndex(game.state) % (batters.length || 1);
  const currentBatter = batters[batterIndex];

  // Hold the BAT tile on the previous batter until both:
  //   1. holdBases releases (resolution animation complete)
  //   2. displayedBases has caught up to the live state (runner trot finished)
  // Without this, the tile flashes to the next batter the instant the server
  // updates the index — before the previous batter has visibly taken their base
  // or been retired. Looks like the next guy is already up before the play ends.
  const [displayedBatter, setDisplayedBatter] = useState(currentBatter);
  useEffect(() => {
    if (holdBases) return;
    if (!basesEqualByIds(displayedBases, game.state.bases)) return;
    if (currentBatter?.id !== displayedBatter?.id) {
      setDisplayedBatter(currentBatter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdBases, displayedBases, basesHash, currentBatter?.id]);

  const batterPos = displayedBatter?.bats === "L" ? "BAT_L" : "BAT_R";
  // Skip subbed-out players when locating the active fielder, otherwise post-substitution
  // the dialog dropdown still points at the player who was just pulled.
  const getFielder = (pos) => fieldingTeam.players.find(p => p.position === pos && !p.subbed_out);
  const isActive = (pos) => activePositions.includes(pos) || (currentPhase === "pitch" && pos === "P");
  const isFieldingPhase = currentPhase === "fielding";
  const isSelectable = (pos) => isFieldingPhase ? activePositions.includes(pos) : undefined;
  const handleMenuToggle = (pos) => setOpenMenu(prev => prev === pos ? null : pos);
  const handleFieldClick = () => { setOpenMenu(null); setOpenRunnerMenu(null); };

  // ── Fielder positions (per-side, persisted in localStorage) ──
  const [homeFielderPositions, setHomeFielderPositions] = useState(() => loadJSON(LS_HOME_FIELDERS));
  const [awayFielderPositions, setAwayFielderPositions] = useState(() => loadJSON(LS_AWAY_FIELDERS));
  useEffect(() => { saveJSON(LS_HOME_FIELDERS, homeFielderPositions); }, [homeFielderPositions]);
  useEffect(() => { saveJSON(LS_AWAY_FIELDERS, awayFielderPositions); }, [awayFielderPositions]);

  const isHomeFielding = game.state.half === "top";
  const currentFielderPositions = isHomeFielding ? homeFielderPositions : awayFielderPositions;
  const setCurrentFielderPositions = isHomeFielding ? setHomeFielderPositions : setAwayFielderPositions;

  const handleFielderDragEnd = (pos, newPosition) => {
    if (newPosition === null) {
      setCurrentFielderPositions(prev => { const u = { ...prev }; delete u[pos]; return u; });
    } else {
      setCurrentFielderPositions(prev => ({ ...prev, [pos]: newPosition }));
    }
  };

  // ── Batter positions (per-side, persisted in localStorage) ──
  const [batterPositions, setBatterPositions] = useState(() => loadJSON(LS_BATTER_POSITIONS));
  useEffect(() => { saveJSON(LS_BATTER_POSITIONS, batterPositions); }, [batterPositions]);

  const isHomeBatting = game.state.half === "bottom";
  const batterSideKey = isHomeBatting ? 'home' : 'away';
  const currentBatterPosition = batterPositions[batterSideKey] || null;
  const handleBatterDragEnd = (pos, newPosition) => {
    setBatterPositions(prev => ({ ...prev, [batterSideKey]: newPosition }));
  };

  // Runner menu — only one runner's dropdown is open at a time. Steals are declared from here.
  const [openRunnerMenu, setOpenRunnerMenu] = useState(null);
  // Close any open runner menu when phase or bases change so a stale dropdown can't linger after a play
  useEffect(() => { setOpenRunnerMenu(null); }, [currentPhase, basesHash]);
  // Drag-persisted runner positions are deprecated; clear the legacy localStorage entry once.
  useEffect(() => { try { localStorage.removeItem(LS_RUNNER_POSITIONS); } catch {} }, []);

  return (
    <div
      ref={containerRef}
      data-testid="diamond-field"
      className="diamond-container relative mx-auto h-full max-w-full aspect-square"
      onClick={handleFieldClick}
    >
      {/* SVG Field Background */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
        <rect width="100" height="100" fill="transparent" />
        <line x1="50" y1="75" x2="5" y2="30" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        <line x1="50" y1="75" x2="95" y2="30" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        <g stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none">
          <line x1="50" y1="75" x2="70" y2="55" />
          <line x1="70" y1="55" x2="50" y2="35" />
          <line x1="50" y1="35" x2="30" y2="55" />
          <line x1="30" y1="55" x2="50" y2="75" />
        </g>
        <circle cx="50" cy="58" r="3" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
        <rect x="68" y="53" width="4" height="4" transform="rotate(45 70 55)"
          className={`transition-colors duration-300 ${displayedBases.first ? 'fill-amber-400' : 'fill-white/90'}`} />
        <rect x="48" y="33" width="4" height="4" transform="rotate(45 50 35)"
          className={`transition-colors duration-300 ${displayedBases.second ? 'fill-amber-400' : 'fill-white/90'}`} />
        <rect x="28" y="53" width="4" height="4" transform="rotate(45 30 55)"
          className={`transition-colors duration-300 ${displayedBases.third ? 'fill-amber-400' : 'fill-white/90'}`} />
        <polygon points="50,77 47,74 47,72 53,72 53,74" fill="white" />
      </svg>

      {/* Pickoff spotlight overlay — semi-transparent dark veil sits above fielders
          (z=10) but below runner tiles (z=100) and the elevated pitcher (z=70),
          so only the interactive targets remain fully lit. */}
      <AnimatePresence>
        {pickoffSelectMode && (
          <motion.div
            key="pickoff-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-slate-950/60 pointer-events-none"
            style={{ zIndex: 50 }}
          />
        )}
        {/* Tough Hop overlay — dims non-eligible fielders (z=10); eligible infielders
            are elevated to z=80 so they remain fully lit above this veil. */}
        {toughHopSelectMode && (
          <motion.div
            key="toughhop-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-slate-950/55 pointer-events-none"
            style={{ zIndex: 50 }}
          />
        )}
        {/* Directional Hitting overlay — dims non-eligible fielders */}
        {directionalHittingSelectMode && (
          <motion.div
            key="directionalhitting-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-slate-950/55 pointer-events-none"
            style={{ zIndex: 50 }}
          />
        )}
      </AnimatePresence>

      {/* Weather/Ballpark */}
      {game.weather && (
        <div className="absolute top-1 left-1 z-[460] flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-md px-2 py-0.5 pointer-events-none">
          <span className="text-sm">{game.weather.emoji}</span>
          <span className="text-[9px] text-slate-300">{game.weather.temp}° {game.weather.condition}</span>
          {game.ballpark && <span className="text-[9px] text-slate-500">| {game.ballpark}</span>}
        </div>
      )}

      {/* Fielders */}
      {["CF", "LF", "RF", "SS", "2B", "3B", "1B"].map(pos => {
        const fielder = getFielder(pos);
        const isInfield = ["SS", "2B", "3B", "1B"].includes(pos);
        const thEligible = toughHopSelectMode && isInfield && !!fielder && toughHopEligibleIds.includes(String(fielder.id));
        const thSelectedToken = thEligible && !!toughHopSelectedId && toughHopSelectedId === String(fielder.id);
        const thHighlight = thEligible && !thSelectedToken;
        const thOnClick = thEligible ? onToughHopSelect : null;
        const dhEligible = directionalHittingSelectMode && !!fielder && directionalHittingEligibleIds.includes(String(fielder.id));
        const dhSelectedToken = dhEligible && !!directionalHittingSelectedId && directionalHittingSelectedId === String(fielder.id);
        const dhHighlight = dhEligible && !dhSelectedToken;
        const dhOnClick = dhEligible ? onDirectionalHittingSelect : null;
        return (
          <FielderToken key={pos} player={fielder} pos={pos} type="fielder"
            isActive={isActive(pos)} isSelectable={isSelectable(pos)} onAction={onFielderAction}
            menuOpen={openMenu === pos} onCloseOthers={handleMenuToggle}
            canEdit={canEditFielding} hotdog={isFielderHotdog(fielder)}
            customPosition={currentFielderPositions[pos]} onDragEnd={handleFielderDragEnd} containerRef={containerRef}
            toughHopHighlight={thHighlight} toughHopSelected={thSelectedToken}
            toughHopColorHex={toughHopColorHex} toughHopOnClick={thOnClick}
            directionalHittingHighlight={dhHighlight} directionalHittingSelected={dhSelectedToken}
            directionalHittingColorHex={directionalHittingColorHex} directionalHittingOnClick={dhOnClick} />
        );
      })}

      {/* Pitcher */}
      {(() => {
        const pitcher = getFielder("P");
        return (
          <FielderToken player={pitcher} pos="P" type="pitcher"
            isActive={isActive("P")} isSelectable={isSelectable("P")}
            onAction={(action, player) => action === 'substitute' ? onPitcherClick?.() : onFielderAction?.(action, player)}
            menuOpen={openMenu === "P"} onCloseOthers={handleMenuToggle}
            canEdit={canEditFielding} hotdog={isFielderHotdog(pitcher)}
            customPosition={currentFielderPositions["P"]} onDragEnd={handleFielderDragEnd} containerRef={containerRef}
            elevated={pickoffSelectMode} />
        );
      })()}

      {/* Catcher */}
      {(() => {
        const catcher = getFielder("C");
        return (
          <FielderToken player={catcher} pos="C" type="fielder"
            isActive={isActive("C")} isSelectable={isSelectable("C")} onAction={onFielderAction}
            menuOpen={openMenu === "C"} onCloseOthers={handleMenuToggle}
            canEdit={canEditFielding} hotdog={isFielderHotdog(catcher)}
            customPosition={currentFielderPositions["C"]} onDragEnd={handleFielderDragEnd} containerRef={containerRef} />
        );
      })()}

      {/* Batter — uses displayedBatter (gated on the runner trot completing) so
          the BAT tile doesn't flash to the next hitter mid-resolution. */}
      <FielderToken player={displayedBatter} pos={batterPos} type="batter"
        isActive={currentPhase === "pitch"}
        canEdit={canEditBatter} hotdog={isBatterHotdog(displayedBatter)}
        customPosition={currentBatterPosition || POSITIONS[batterPos]}
        onDragEnd={handleBatterDragEnd} containerRef={containerRef} />

      {/* Runners — uses displayedBases (animated step-through) instead of live state.
          Click opens a dropdown with "Declare Steal" — disabled while a pitch is mid-resolution. */}
      <AnimatePresence>
        {displayedBases.first && (
          <Runner key={`runner-first-${displayedBases.first.id}`} base="first" runner={displayedBases.first}
            canSteal={canSteal} menuOpen={openRunnerMenu === "first"} onMenuToggle={setOpenRunnerMenu}
            onDeclareSteal={onDeclareSteal}
            onShowDetails={(player) => onFielderAction?.('details', player)}
            pickoffSelectMode={pickoffSelectMode}
            isPickoffTarget={pickoffSelectedBase === "first"}
            onPickoffSelect={onPickoffSelectRunner} />
        )}
        {displayedBases.second && (
          <Runner key={`runner-second-${displayedBases.second.id}`} base="second" runner={displayedBases.second}
            canSteal={canSteal} menuOpen={openRunnerMenu === "second"} onMenuToggle={setOpenRunnerMenu}
            onDeclareSteal={onDeclareSteal}
            onShowDetails={(player) => onFielderAction?.('details', player)}
            pickoffSelectMode={pickoffSelectMode}
            isPickoffTarget={pickoffSelectedBase === "second"}
            onPickoffSelect={onPickoffSelectRunner} />
        )}
        {displayedBases.third && (
          <Runner key={`runner-third-${displayedBases.third.id}`} base="third" runner={displayedBases.third}
            canSteal={canSteal} menuOpen={openRunnerMenu === "third"} onMenuToggle={setOpenRunnerMenu}
            onDeclareSteal={onDeclareSteal}
            onShowDetails={(player) => onFielderAction?.('details', player)}
            pickoffSelectMode={pickoffSelectMode}
            isPickoffTarget={pickoffSelectedBase === "third"}
            onPickoffSelect={onPickoffSelectRunner} />
        )}
        {displayedBases.home && (
          <Runner key={`runner-home-${displayedBases.home.id}`} base="home" runner={displayedBases.home} />
        )}
      </AnimatePresence>

      {/* Phase Clock — left of home plate, mirroring the stats widget on the right.
          Same widget shows pitch / fielding / steal countdowns; label adapts to the active kind.
          During GM pause, the server clears the clock entry, so this just renders nothing —
          the GM control bar (greyed pause + active play) is the pause indicator. */}
      {pitchClock !== null && (
        <div className={`absolute bottom-16 left-2 z-[460] bg-black/60 backdrop-blur-sm rounded-lg p-2 border min-w-[80px] text-center ${pitchClock <= 10 ? "border-red-500/60" : "border-slate-700/50"}`}>
          <div className="text-[8px] font-bold text-slate-500 tracking-wider mb-1">
            {pitchClockKind === "steal" ? "STEAL CLOCK" : pitchClockKind === "fielding" ? "FIELD CLOCK" : pitchClockKind === "pitch_batter" ? "BATTING TIMER" : "PITCH CLOCK"}
          </div>
          <div className={`font-mono font-black tabular-nums text-2xl ${pitchClock <= 10 ? "text-red-400 animate-pulse" : "text-slate-200"}`}>
            {pitchClock}
          </div>
        </div>
      )}

      {/* Game Stats Widget */}
      <div className="absolute bottom-16 right-2 z-[460] bg-black/60 backdrop-blur-sm rounded-lg p-2 min-w-[140px] border border-slate-700/50">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[8px] font-bold text-slate-500 tracking-wider">BOX SCORE</div>
          <button onClick={onStatsClick} className="p-0.5 rounded hover:bg-slate-700/50 text-slate-500 hover:text-amber-400 transition-colors">
            <Info size={12} />
          </button>
        </div>
        <div className="space-y-1.5">
          {/* All pitchers for the fielding team that have pitched or are currently active */}
          {(() => {
            const activePitcherId = getFielder("P")?.id;
            const allPitchers = fieldingTeam.players.filter(p => p.position === "P");
            const visiblePitchers = allPitchers.filter(p =>
              p.id === activePitcherId || (gameStats.players?.[p.id] && gameStats.players[p.id].outs > 0)
            );
            return visiblePitchers.map(pitcher => {
              const isActive = pitcher.id === activePitcherId;
              const pStats = gameStats.players?.[pitcher.id] || { k: 0, bb: 0, h: 0, outs: 0, runsAllowed: 0 };
              const era = formatEra(pStats.runsAllowed, pStats.outs);
              return (
                <div key={pitcher.id} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded flex items-center justify-center text-[7px] font-bold ${isActive ? 'bg-red-500/20 text-red-400' : 'bg-slate-700/40 text-slate-500'}`}>P</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1">
                      <div className={`text-[9px] truncate ${isActive ? 'text-slate-400' : 'text-slate-600'}`}>{getLastName(pitcher.name)}</div>
                      {!isActive && <div className="text-[7px] text-slate-600">prev</div>}
                    </div>
                    <div className="flex gap-1.5 text-[8px]">
                      <span className="text-slate-500">SO:<span className="text-red-400 font-bold ml-0.5">{pStats.k}</span></span>
                      <span className="text-slate-500">BB:<span className="text-blue-400 font-bold ml-0.5">{pStats.bb}</span></span>
                      <span className="text-slate-500">H:<span className="text-amber-400 font-bold ml-0.5">{pStats.h}</span></span>
                      <span className="text-slate-500">ERA:<span className="text-white font-bold ml-0.5">{era}</span></span>
                    </div>
                  </div>
                </div>
              );
            });
          })()}
          {/* Current batter — uses displayedBatter so the stats widget stays
              in lock-step with the BAT tile on the field. */}
          {(() => {
            const batterId = displayedBatter?.id;
            const bStats = gameStats.players?.[batterId] || { ab: 0, h: 0, k: 0, rbi: 0 };
            return (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-500/20 flex items-center justify-center text-[7px] font-bold text-amber-400">B</div>
                <div className="flex-1">
                  <div className="text-[9px] text-slate-400 truncate">{getLastName(displayedBatter?.name)}</div>
                  <div className="flex gap-1.5 text-[8px]">
                    <span className="text-slate-500">AB:<span className="text-slate-300 font-bold ml-0.5">{bStats.ab}</span></span>
                    <span className="text-slate-500">H:<span className="text-emerald-400 font-bold ml-0.5">{bStats.h}</span></span>
                    <span className="text-slate-500">SO:<span className="text-red-400 font-bold ml-0.5">{bStats.k}</span></span>
                    <span className="text-slate-500">RBI:<span className="text-amber-400 font-bold ml-0.5">{bStats.rbi || 0}</span></span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Hotdog floaters — 🌭 rises and fades from each tile when the John Kruk card plays.
          Fielding team: cascades across all 9 positions. Batting: batter only. */}
      {hotdogFloaters.map(f => {
        const pos = f.pos === "__batter__"
          ? (currentBatterPosition || POSITIONS[batterPos])
          : (currentFielderPositions[f.pos] || POSITIONS[f.pos]);
        if (!pos) return null;
        return (
          <div
            key={f.id}
            className="absolute pointer-events-none select-none"
            style={{ left: `${pos.x}%`, top: `${pos.y}%`, zIndex: 25, transform: 'translate(-50%, -50%)' }}
          >
            <motion.span
              initial={{ y: 0, opacity: 0, scale: 0.8 }}
              animate={{ y: [-4, -56], opacity: [0, 1, 1, 0], scale: [0.8, 1.05, 1.0] }}
              transition={{
                duration: RISE_DURATION,
                delay: f.delay,
                ease: "easeOut",
                times: [0, 0.12, 0.55, 1],
              }}
              style={{ display: "block", fontSize: "1.4rem", lineHeight: 1 }}
            >
              🌭
            </motion.span>
          </div>
        );
      })}
    </div>
  );
}

// Wrapped in memo so DiamondField doesn't re-render on unrelated GameBoard renders. Stats /
// hand-card / log updates trigger GameBoard re-renders dozens of times per at-bat; this widget
// only needs to re-render when its own props change. Caller should pass stable callback refs
// (useCallback in GameBoard) for the action handlers.
export default memo(DiamondField);
